// src/lib/text.js
//
// Gentle auto-capitalisation for fields staff type at speed.
//
// Two rules make this safe to run on every keystroke:
//
//   1. It only ever promotes a lowercase letter to uppercase. Nothing the
//      person typed is lowercased, so MacLeod, O'Brien and ALLCAPS survive.
//   2. The string length never changes, so the caret doesn't jump mid-word.
//
// It also leaves alone anything that looks deliberately cased — "iPhone",
// "eBay" — where a blind uppercase would produce "IPhone".

/** An uppercase second letter means the casing was chosen — MacLeod, iPhone. */
function deliberatelyCased(word) {
  return word.length > 1 && word[1] === word[1].toUpperCase() && /[A-Za-z]/.test(word[1]);
}

/**
 * Capitalise each word of a name. Hyphens and apostrophes start a new word, so
 * "mary-jane o'brien" becomes "Mary-Jane O'Brien".
 */
export function titleCaseName(s) {
  if (!s) return s;
  return String(s).replace(/[A-Za-zÀ-ÿ]+/g, (word, offset, full) => {
    if (deliberatelyCased(word)) return word;

    // A lone letter normally waits for the next keystroke: this runs as you
    // type, so the "i" of "iPhone" would become "I" before the "P" arrived and
    // could never recover. The exception is a letter followed by an apostrophe
    // or hyphen — that's a compound name like O'Brien, where the single letter
    // really is a word of its own.
    const next = full[offset + word.length] || '';
    const compound = next === "'" || next === '’' || next === '-';
    if (word.length < 2 && !compound) return word;

    return word[0].toUpperCase() + word.slice(1);
  });
}

/**
 * Capitalise the first letter of the text, and the first letter after a
 * sentence ending. Used for card messages and free-text notes, where
 * title-casing every word would look wrong ("Happy Anniversary Bro").
 */
export function sentenceCase(s) {
  if (!s) return s;
  let out = String(s);
  // Start of the text, or after . ! ? followed by whitespace. Also after a
  // newline, since messages are often written as separate lines.
  out = out.replace(/(^|[.!?]\s+|\n\s*)([a-zà-ÿ])/g,
    (m, lead, ch) => lead + ch.toUpperCase());
  return out;
}
